{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            Copyright (c) 2022                                      }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit TimeSpan;

interface

type
  TTimeSpan = record
  private
    FTicks: Int64;
  strict private
    function GetDays: Integer;
    function GetHours: Integer;
    function GetMinutes: Integer;
    function GetSeconds: Integer;
    function GetMilliseconds: Integer;
    function GetTotalDays: Double;
    function GetTotalHours: Double;
    function GetTotalMinutes: Double;
    function GetTotalSeconds: Double;
    function GetTotalMilliseconds: Double;
    class function GetScaledInterval(const AValue: Double; const AScale: Integer): TTimeSpan; static;
    class constructor Create;
    class function get_MaxValue: TTimeSpan; static;
    class function get_MinValue: TTimeSpan; static;
    class function get_Zero: TTimeSpan; static;
  strict private class var
    FMinValue: Int64;
    FMaxValue: Int64;
    FZero: Int64;
  strict private const
    cIntLengthShift = 52 - 1;
    MillisecondsPerTick = 0.0001;
    SecondsPerTick = 1e-07;
    MinutesPerTick = 1.6666666666666667E-09;
    HoursPerTick = 2.7777777777777777E-11;
    DaysPerTick = 1.1574074074074074E-12;
    MillisPerSecond = 1000;
    MillisPerMinute = 60 * MillisPerSecond;
    MillisPerHour = 60 * MillisPerMinute;
    MillisPerDay = 24 * MillisPerHour;
    MaxSeconds = 922337203685;
    MinSeconds = -922337203685;
    MaxMilliseconds = 922337203685477;
    MinMilliseconds = -922337203685477;
  public const
    TicksPerMillisecond = 10000;
    TicksPerSecond = 1000 * Int64(TicksPerMillisecond);
    TicksPerMinute = 60 * Int64(TicksPerSecond);
    TicksPerHour = 60 * Int64(TicksPerMinute);
    TicksPerDay = 24 * TicksPerHour;
  public
    constructor Create(ATicks: Int64); overload;
    constructor Create(const AHours, AMinutes, ASeconds: Integer); overload;
    constructor Create(const ADays, AHours, AMinutes, ASeconds: Integer); overload;
    constructor Create(const ADays, AHours, AMinutes, ASeconds, AMilliseconds: Integer); overload;

    function Add(const ATimeSpan: TTimeSpan): TTimeSpan; overload;
    function Duration: TTimeSpan;
    function Negate: TTimeSpan;
    function Subtract(const ATimeSpan: TTimeSpan): TTimeSpan; overload;
    function ToString: string;

    class function FromDays(const AValue: Double): TTimeSpan; static;
    class function FromHours(const AValue: Double): TTimeSpan; static;
    class function FromMinutes(const AValue: Double): TTimeSpan; static;
    class function FromSeconds(const AValue: Double): TTimeSpan; static;
    class function FromMilliseconds(const AValue: Double): TTimeSpan; static;
    class function FromTicks(const AValue: Int64): TTimeSpan; static;
    class function Subtract(const ADateTimeLeft, ADateTimeRight: TDateTime): TTimeSpan; overload; static;

    class function Parse(const AValue: string): TTimeSpan; static;
    class function TryParse(const AValue: string; out ATimeSpan: TTimeSpan): Boolean; static;

    property Ticks: Int64 read FTicks;
    property Days: Integer read GetDays;
    property Hours: Integer read GetHours;
    property Minutes: Integer read GetMinutes;
    property Seconds: Integer read GetSeconds;
    property Milliseconds: Integer read GetMilliseconds;
    property TotalDays: Double read GetTotalDays;
    property TotalHours: Double read GetTotalHours;
    property TotalMinutes: Double read GetTotalMinutes;
    property TotalSeconds: Double read GetTotalSeconds;
    property TotalMilliseconds: Double read GetTotalMilliseconds;

    class property MinValue: TTimeSpan read get_MinValue;
    class property MaxValue: TTimeSpan read get_MaxValue;
    class property Zero: TTimeSpan read get_Zero;
  end;

implementation

uses
  SysUtils, Math;

resourcestring
  SCannotNegateTimespan = 'Negating the minimum value of a Timespan is invalid';
  SInvalidTimespanDuration = 'The duration cannot be returned because the absolute value exceeds the value of TTimeSpan.MaxValue';
  SInvalidTimespanFormat = 'Invalid Timespan format';
  STimespanElementTooLong = 'Timespan element too long';
  STimespanTooLong = 'Timespan too long';
  STimespanValueCannotBeNan = 'Value cannot be NaN';

type
  TTimeSpanParser = record
  public type
    TParseError = (peNone, peFormat, peOverflow, peOverflowHMS);
  private
    FStr: string;
    FPos: Integer;
  public
    function CurrentChar: Char; inline;
    function NextChar: Char; inline;
    function NextNonDigit: Char;
    function Convert(const AValue: string): Int64;
    function TryConvert(const AString: string; out AValue: Int64): TParseError;
    function NextInt(AMaxValue: Integer; out AValue: Integer): TParseError;
    function ConvertTime(out ATime: Int64): TParseError;
    procedure SkipWhite;
  end;

{ TTimeSpanParser }

function TTimeSpanParser.CurrentChar: Char;
begin
  if (FPos>= Low(string)) and (FPos <= High(FStr)) then
    Result := FStr[FPos]
  else
    Result := #0;
end;

function TTimeSpanParser.NextChar: Char;
begin
  if FPos <= High(FStr) then
    Inc(FPos);
  Result := CurrentChar;
end;

function TTimeSpanParser.NextNonDigit: Char;
var
  lCount: Integer;
begin
  for lCount := FPos to High(FStr) do
  begin
    Result := FStr[lCount];
    if (Result < '0') or (Result > '9') then
      Exit;
  end;
  Result := #0;
end;

function TTimeSpanParser.Convert(const AValue: string): Int64;
begin
  Result := 0;
  case TryConvert(AValue, Result) of
    peFormat:
      raise Exception.Create(sInvalidTimespanFormat);
    peOverflow:
      raise EIntOverflow.Create(sTimespanTooLong);
    peOverflowHMS:
      raise EIntOverflow.Create(sTimespanElementTooLong);
  end;
end;

function TTimeSpanParser.NextInt(AMaxValue: Integer; out AValue: Integer): TParseError;
var
  lChar: Char;
  lStartPos: Integer;
begin
  AValue := 0;
  lStartPos := FPos;
  lChar := CurrentChar;
  while (lChar >= '0') and (lChar <= '9') do
  begin
    if AValue and $F0000000 <> 0 then
      Exit(peOverflow);
    AValue := AValue * 10 + (Ord(lChar) - $30);
    if AValue < 0 then
      Exit(peOverflow);
    lChar := NextChar;
  end;
  if FPos = lStartPos then
    Exit(peFormat);
  if AValue > AMaxValue then
    Exit(peOverflow);
  Result := peNone;
end;

function TTimeSpanParser.ConvertTime(out ATime: Int64): TParseError;
var
  lChar: Char;
  lPart: Integer;
begin
  ATime := 0;
  Result := NextInt(23, lPart);
  if Result <> peNone then
  begin
    if Result = peOverflow then
      Result := peOverflowHMS;
    Exit;
  end;
  ATime := lPart * TTimeSpan.TicksPerHour;
  if CurrentChar <> ':' then
    Exit(peFormat);
  NextChar;
  Result := NextInt(59, lPart);
  if Result <> peNone then
  begin
    if Result = peOverflow then
      Result := peOverflowHMS;
    Exit;
  end;
  ATime := ATime + lPart * TTimeSpan.TicksPerMinute;
  if CurrentChar = ':' then
  begin
    if NextChar <> '.' then
    begin
      Result := NextInt(59, lPart);
      if Result <> peNone then
      begin
        if Result = peOverflow then
          Result := peOverflowHMS;
        Exit;
      end;
      ATime := ATime + lPart * TTimeSpan.TicksPerSecond;
    end;
    if CurrentChar = '.' then
    begin
      lChar := NextChar;
      lPart := TTimeSpan.TicksPerSecond;
      while (lPart > 1) and ((lChar >= '0') and (lChar <= '9')) do
      begin
        lPart := lPart div 10;
        ATime := ATime + (Ord(lChar) - $30) * lPart;
        lChar := NextChar;
      end;
    end;
  end;
  Result := peNone;
end;

procedure TTimeSpanParser.SkipWhite;
var
  lChar: Char;
begin
  lChar := CurrentChar;
  while (lChar= ' ') or (lChar = #9) do
    lChar := NextChar;
end;

function TTimeSpanParser.TryConvert(const AString: string; out AValue: Int64): TParseError;
var
  lDays: Integer;
  lIsNegative: Boolean;
  lTicks: Int64;
  lTimeVal: Int64;
begin
  lTicks := 0;
  AValue := 0;
  FStr := AString;
  FPos := Low(string);
  SkipWhite;
  lIsNegative := False;
  if CurrentChar = '-' then
  begin
    lIsNegative := True;
    NextChar;
  end;
  if NextNonDigit = ':' then
  begin
    Result := ConvertTime(lTicks);
    if Result <> peNone then
      Exit;
  end
  else
  begin
    Result := NextInt($a2e3ff, lDays);
    if Result <> peNone then
      Exit;
    lTicks := lDays * TTimeSpan.TicksPerDay;
    if CurrentChar = '.' then
    begin
      NextChar;
      Result := ConvertTime(lTimeVal);
      if Result <> peNone  then
        Exit;
      lTicks := lTicks + lTimeVal;
    end;
  end;
  if lIsNegative then
  begin
    lTicks := -lTicks;
    if lTicks > 0 then
      Exit(peOverflow);
  end
  else if lTicks < 0 then
    Exit(peOverflow);
  SkipWhite;
  if FPos <= High(FStr) then
    Exit(peFormat);
  AValue := lTicks;
  Result := peNone;
end;

{ TTimeSpan }

constructor TTimeSpan.Create(ATicks: Int64);
begin
  FTicks := ATicks;
end;

constructor TTimeSpan.Create(const AHours, AMinutes, ASeconds: Integer);
begin
  FTicks := Int64(AHours) * SecsPerHour + Int64(AMinutes) * SecsPerMin + ASeconds;
  if (FTicks > MaxSeconds) or (FTicks < MinSeconds) then
    raise EArgumentOutOfRangeException.Create(STimespanTooLong);
  FTicks := FTicks * TicksPerSecond;
end;

constructor TTimeSpan.Create(const ADays, AHours, AMinutes, ASeconds: Integer);
begin
  Create(ADays, AHours, AMinutes, ASeconds, 0);
end;

constructor TTimeSpan.Create(const ADays, AHours, AMinutes, ASeconds, AMilliseconds: Integer);
var
  lTicks: Int64;
begin
  lTicks := (Int64(ADays) * SecsPerDay + Int64(AHours) * SecsPerHour + Int64(AMinutes) * SecsPerMin + ASeconds) * MillisPerSecond + AMilliseconds;
  if (lTicks > MaxMilliseconds) or (lTicks < MinMilliseconds) then
    raise EArgumentOutOfRangeException.Create(sTimespanTooLong);
  FTicks := lTicks * TicksPerMillisecond;
end;

function TTimeSpan.Add(const ATimeSpan: TTimeSpan): TTimeSpan;
var
  lNewTicks: Int64;
begin
  lNewTicks := FTicks + ATimeSpan.FTicks;
  if ((FTicks shr cIntLengthShift) = (ATimeSpan.FTicks shr cIntLengthShift)) and ((FTicks shr cIntLengthShift) <> (lNewTicks shr cIntLengthShift)) then
    raise EArgumentOutOfRangeException.Create(STimespanTooLong);
  Result := TTimeSpan.Create(lNewTicks);
end;

function TTimeSpan.Duration: TTimeSpan;
begin
  if FTicks = MinValue.FTicks then
    raise EIntOverflow.Create(SInvalidTimespanDuration);
  if FTicks < 0 then
    Result := TTimeSpan.Create(-FTicks)
  else
    Result := TTimeSpan.Create(FTicks);
end;

class function TTimeSpan.FromDays(const AValue: Double): TTimeSpan;
begin
  Result := GetScaledInterval(AValue, MillisPerDay);
end;

class function TTimeSpan.FromHours(const AValue: Double): TTimeSpan;
begin
  Result := GetScaledInterval(AValue, MillisPerHour);
end;

class function TTimeSpan.FromMilliseconds(const AValue: Double): TTimeSpan;
begin
  Result := GetScaledInterval(AValue, 1);
end;

class function TTimeSpan.FromMinutes(const AValue: Double): TTimeSpan;
begin
  Result := GetScaledInterval(AValue, MillisPerMinute);
end;

class function TTimeSpan.FromSeconds(const AValue: Double): TTimeSpan;
begin
  Result := GetScaledInterval(AValue, MillisPerSecond);
end;

class function TTimeSpan.FromTicks(const AValue: Int64): TTimeSpan;
begin
  Result := TTimeSpan.Create(AValue);
end;

function TTimeSpan.GetDays: Integer;
begin
  Result := Integer(FTicks div TicksPerDay);
end;

function TTimeSpan.GetHours: Integer;
begin
  Result := Integer((FTicks div TicksPerHour) mod HoursPerDay);
end;

function TTimeSpan.GetMilliseconds: Integer;
begin
  Result := Integer((FTicks div TicksPerMillisecond) mod MillisPerSecond);
end;

function TTimeSpan.GetMinutes: Integer;
begin
  Result := Integer((FTicks div TicksPerMinute) mod MinsPerHour);
end;

function TTimeSpan.GetSeconds: Integer;
begin
  Result := Integer((FTicks div TicksPerSecond) mod SecsPerMin);
end;

function TTimeSpan.GetTotalDays: Double;
begin
  Result := FTicks * DaysPerTick;
end;

function TTimeSpan.GetTotalHours: Double;
begin
  Result := FTicks * HoursPerTick;
end;

function TTimeSpan.GetTotalMilliseconds: Double;
begin
  Result := MillisecondsPerTick;
  Result := FTicks * Result;
  if Result > MaxMilliseconds then
    Result := MaxMilliseconds
  else if Result < MinMilliseconds then
    Result := MinMilliseconds;
end;

function TTimeSpan.GetTotalMinutes: Double;
begin
  Result := FTicks * MinutesPerTick;
end;

function TTimeSpan.GetTotalSeconds: Double;
begin
  Result := FTicks * SecondsPerTick;
end;

class constructor TTimeSpan.Create;
begin
  FMinValue := High(Int64);
  FMaxValue := Low(Int64);
end;

class function TTimeSpan.GetScaledInterval(const AValue: Double; const AScale: Integer): TTimeSpan;
var
  lNewValue: Double;
begin
  if IsNan(AValue) then
    raise EArgumentException.Create(STimespanValueCannotBeNan);
  lNewValue := AValue * AScale;
  if AValue >= 0.0 then
    lNewValue := lNewValue + 0.5
  else
    lNewValue := lNewValue - 0.5;
  if (lNewValue > MaxMilliseconds) or (lNewValue < MinMilliseconds) then
    raise EArgumentOutOfRangeException.Create(STimespanElementTooLong);
  Result := TTimeSpan.Create(Trunc(lNewValue) * TicksPerMillisecond);
end;

class function TTimeSpan.get_MaxValue: TTimeSpan;
begin
  Result.FTicks := FMaxValue;
end;

class function TTimeSpan.get_MinValue: TTimeSpan;
begin
  Result.FTicks := FMinValue;
end;

class function TTimeSpan.get_Zero: TTimeSpan;
begin
  Result.FTicks := FZero;
end;

function TTimeSpan.Negate: TTimeSpan;
begin
  if FTicks = MinValue.FTicks then
    raise EIntOverflow.Create(SCannotNegateTimespan);
  Result := TTimeSpan.Create(-FTicks);
end;

class function TTimeSpan.Parse(const AValue: string): TTimeSpan;
var
  lParser: TTimeSpanParser;
begin
  Result := TTimeSpan.Create(lParser.Convert(AValue));
end;

function TTimeSpan.Subtract(const ATimeSpan: TTimeSpan): TTimeSpan;
var
  lNewTicks: Int64;
begin
  lNewTicks := FTicks - ATimeSpan.FTicks;
  if ((FTicks shr cIntLengthShift) <> (ATimeSpan.FTicks shr cIntLengthShift)) and ((FTicks shr cIntLengthShift) <> (lNewTicks shr cIntLengthShift)) then
    raise EArgumentOutOfRangeException.Create(sTimespanTooLong);
  Result := TTimeSpan.Create(lNewTicks);
end;

function TTimeSpan.ToString: string;
var
  lFormat: string;
  lDays: Int64;
  lSecondTicks: Int64;
  lTicks: Int64;
begin
  lFormat := '%1:.2d:%2:.2d:%3:.2d';
  lDays := FTicks div TicksPerDay;
  lTicks := FTicks mod TicksPerDay;
  if FTicks < 0 then
    lTicks := -lTicks;
  if lDays <> 0 then
    lFormat := '%0:d.' + lFormat;
  lSecondTicks := lTicks mod TicksPerSecond;
  if lSecondTicks <> 0 then
    lFormat := lFormat + '.%4:.7d';
  Result := string.Format(lFormat, [lDays, (lTicks div TicksPerHour) mod HoursPerDay,
    (lTicks div TicksPerMinute) mod MinsPerHour, (lTicks div TicksPerSecond) mod SecsPerMin,
    lSecondTicks]);
end;

class function TTimeSpan.TryParse(const AValue: string; out ATimeSpan: TTimeSpan): Boolean;
var
  lTicks: Int64;
  lParser: TTimeSpanParser;
begin
  Result := lParser.TryConvert(AValue, lTicks) = TTimeSpanParser.TParseError.peNone;
  if Result then
    ATimeSpan := TTimeSpan.Create(lTicks)
  else
    ATimeSpan := Zero;
end;

class function TTimeSpan.Subtract(const ADateTimeLeft, ADateTimeRight: TDateTime): TTimeSpan;
begin
  Result := TTimeSpan.Create(Trunc(TimeStampToMSecs(DateTimeToTimeStamp(ADateTimeLeft)) - TimeStampToMSecs(DateTimeToTimeStamp(ADateTimeRight))) * TicksPerMillisecond);
end;

end.

